/**
 * Online-payment transaction receipt — bitmap renderer.
 *
 * Ports the layout of the legacy `generateOnlinePaymentReceiptString` (38-char
 * text) to the shared canvas toolkit. The `transformTransactionToReceiptData`
 * mapper that produces `OnlinePaymentReceiptData` is unchanged and still lives
 * in `src/online-payment/utils/transactionToReceipt.ts`.
 */
import { fCurrency } from 'src/utils/format-number';

import type { OnlinePaymentReceiptData } from 'src/online-payment/utils/transactionToReceipt';

import type { PaperWidth } from './types';
import { createReceiptCanvas, appendBrandingFooter } from './canvas-toolkit';

/** Formats a payment-method key for display. */
function formatPaymentMethod(method: string): string {
  const methodMap: Record<string, string> = {
    card: 'Credit Card',
    ach: 'Bank Transfer (ACH)',
    ach_debit: 'ACH Debit',
    ach_credit: 'ACH Credit',
    cash: 'Cash',
    check: 'Check',
  };
  return methodMap[method.toLowerCase()] || method;
}

export function onlinePaymentReceiptCanvas(
  data: OnlinePaymentReceiptData,
  opts: { paperWidth?: PaperWidth } = {}
): string {
  const c = createReceiptCanvas({ paperWidth: opts.paperWidth });
  const { px } = c;

  const dateStr = data.date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  const timeStr = data.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  // ── Header ──────────────────────────────────────────────────────────────────
  c.lines(data.businessName.toUpperCase(), 32, 700, 'center');
  c.lines(data.address.street1, 24, 400, 'center');
  c.lines(
    `${data.address.city}, ${data.address.state} ${data.address.postalCode}`,
    24,
    400,
    'center'
  );
  c.space(px(8));

  c.rule();
  c.lines(`TRANSACTION: ${data.shortIdentifier}`, 26, 700, 'center');
  c.rule();

  c.lines(`Date: ${dateStr}`, 24, 400, 'left');
  c.lines(`Time: ${timeStr}`, 24, 400, 'left');

  c.rule();
  c.lines('ONLINE PAYMENT', 28, 700, 'center');
  c.rule();

  if (data.description) {
    c.lines('Description:', 24, 600, 'left');
    c.lines(data.description, 24, 400, 'left');
    c.space(px(6));
  }

  c.rule();
  c.row('Payment Method', formatPaymentMethod(data.paymentMethod), 26, 400);
  c.rule();
  c.row('TOTAL', fCurrency(data.amount / 100), 32, 700);
  c.rule();

  if (data.customer) {
    c.lines('CUSTOMER INFORMATION', 24, 700, 'left');
    c.space(px(4));
    c.lines(`Name: ${data.customer.name}`, 24, 400, 'left');
    if (data.customer.phone) c.lines(`Phone: ${data.customer.phone}`, 24, 400, 'left');
    if (data.customer.email) c.lines(`Email: ${data.customer.email}`, 24, 400, 'left');
  }

  c.space(px(16));
  c.lines('Thank you for your payment!', 24, 600, 'center');

  appendBrandingFooter(c);
  return c.finish();
}
